import LessonActionIcon from './LessonActionIcon';
import { questionAction } from '../../lib/lessonAction';
// TransitionCurtain — contextual loading screen that previews what's coming next.
// Matches the visual style of RevealAnswers' "lets-see" overlay: bg-wine-primary,
// display heading, optional question card. Static: the copy is the whole
// message (no looping dots since the UX review, 3 Nov 2026).
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
// Pure tokens (no gsap side-effects) — this file only uses framer-motion.
import { DUR, prefersReducedMotion } from '../../lib/anim-tokens';
import { FLOW_COPY, pickQuestionCurtainCopy, curtainFallbackHeading, settingUpLine } from '../../lib/flowCopy';
import { useGameFlow } from '../../contexts/GameFlowProvider';
import { isScoreboardBlackout } from '../../lib/scoreboardBlackout';
import { lazyWithRetry } from '../../utils/lazyRetry';
// Lazy: FirstIcon pulls in gsap and lib/anims, and this curtain sits in the
// eager shell through GameWrapper. Loading the icon on demand keeps gsap out
// of the shell for /lobby, /avatar, /waitingroom and /tv; the icon only shows
// on the personalised scoreboard curtain below.
const FirstIcon = lazyWithRetry(() => import('./FirstIcon'));
import ratingImg from '../../assets/illustrations/rating.webp';

// ── How long the loading state is allowed to be true for ────────────────────
//
// On the venue wifi the prefetch is genuinely slow and the bar earns its place.
// On a good connection the same bar appears and vanishes inside a tenth of a
// second, which reads as a glitch rather than as progress — the eye catches
// that something happened and cannot tell what. So the loading state has a
// floor, and finishing is a state of its own rather than an absence.
//
// The curtain itself already sits for MIN_CURTAIN_MS (2s), so both of these
// fit inside a beat the room is waiting through anyway.
const MIN_LOADING_MS = 1000;
const DONE_MS = 900;

/**
 * Turns the raw {loaded, total} — which arrives, changes and then becomes null
 * — into something legible: loading, then done, then gone.
 *
 * @returns {{phase: 'idle'|'loading'|'done', loaded: number, total: number}}
 */
function useLoadingPhase(progress) {
  const [phase, setPhase] = useState('idle');
  // A fraction, and it only ever goes up.
  //
  // The provider reports {loaded, total} per prefetch, and a curtain can cover
  // more than one: a second batch starts at 0 of 4 while the first finished at
  // 6 of 6, so the bar emptied and refilled in front of the room (Mel: "it
  // grew and then shrank down to zero super quickly... it should always just
  // grow or fill up and not go back to zero before the screen moves"). A bar
  // that goes backwards is worse than no bar — it reads as work being undone.
  // So what is drawn is the high-water mark for as long as this curtain is up,
  // reset only when the whole thing goes idle.
  const [fraction, setFraction] = useState(0);
  const startedAtRef = useRef(0);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  useEffect(() => clearTimers, []);

  const active = Boolean(progress && progress.total > 1);
  const loaded = progress?.loaded ?? 0;
  const total = progress?.total ?? 0;

  useEffect(() => {
    if (active) {
      clearTimers();
      if (!startedAtRef.current) startedAtRef.current = Date.now();
      const next = total > 0 ? loaded / total : 0;
      setFraction((held) => Math.max(held, next));
      setPhase('loading');
      return;
    }
    // Nothing in flight. If we never showed the loading state there is nothing
    // to finish — an idle curtain must not flash "ready" at a player who was
    // never told anything was loading.
    if (!startedAtRef.current) return;

    const held = Date.now() - startedAtRef.current;
    startedAtRef.current = 0;
    clearTimers();
    timersRef.current.push(setTimeout(() => {
      setPhase('done');
      timersRef.current.push(setTimeout(() => {
        setPhase('idle');
        setFraction(0); // the next curtain starts its own bar
      }, DONE_MS));
    }, Math.max(0, MIN_LOADING_MS - held)));
  }, [active, loaded, total]);

  // Finishing always shows a full bar, whatever the last numbers said: the
  // point of the done state is that the wait is over.
  return { phase, fraction: phase === 'done' ? 1 : fraction };
}


// TAS-274: this curtain is shared with the phone, where the sizes below are
// right for arm's length. On the big screen the SAME panel is read from across
// a room, so every element steps up a tier or two. Held as one table rather
// than ternaries scattered through the branches, so the two sets stay
// comparable at a glance and no branch can drift out of step. `phone` is the
// pre-TAS-274 markup exactly, so phone mode is unchanged.
const SIZES = {
  phone: {
    emoji: 'text-5xl mb-4',
    icon: 'h-20 w-20 object-contain',
    questionIcon: 'h-24 w-24 object-contain',
    leaderIcon: 'h-16 w-16 object-contain',
    iconWrap: 'mb-5',
    heading: 'text-3xl sm:text-4xl',
    // Party curtains wear the same type as the default curtain and Game's
    // round-intro: a Fraunces heading over DESIGN.md's `display-sm` Fraunces
    // italic supporting line, not the old all-caps Outfit label (Mel,
    // 2026-10-01).
    sub: 'mt-3 text-display-sm',
    pad: 'px-8',
    panel: 'relative text-center',
    // Default (non-party) curtain. The heading is `display-xl` — DESIGN.md's
    // "main reveal headlines" — and NOT `display-lg`: this same copy also
    // appears on Game's "All players have submitted" screen and on
    // RevealAnswers' "Let's see what everyone guessed" overlay, seconds apart
    // on the same green. At two different sizes the room saw one sentence
    // resize itself mid-transition. Keep all three in step.
    defaultPad: 'px-6 sm:px-8',
    column: 'w-full max-w-md',
    defaultHeading: 'text-display-xl mb-6',
    questionCard: 'rounded-xl px-5 py-4 mb-6',
    cardBorder: 'border',
    questionText: 'text-lg sm:text-xl',
  },
  tv: {
    emoji: 'text-9xl mb-10',
    icon: 'h-48 w-48 object-contain',
    questionIcon: 'h-56 w-56 object-contain',
    leaderIcon: 'h-44 w-44 object-contain',
    iconWrap: 'mb-12',
    heading: 'text-8xl',
    sub: 'mt-10 text-4xl',
    pad: 'px-20',
    // At text-8xl a two-name line ("Alice and Bob are neck and neck!") runs off
    // the edge of a 16:9 screen, so the TV panel is width-capped where the
    // phone's never needed to be.
    panel: 'relative text-center max-w-6xl',
    defaultPad: 'px-20',
    column: 'w-full max-w-6xl',
    defaultHeading: 'text-8xl font-bold leading-tight mb-12',
    questionCard: 'rounded-3xl px-12 py-10 mb-12',
    // A 1px hairline disappears at TV scale — the border steps up with
    // everything else so the card keeps its edge from across the room.
    cardBorder: 'border-2',
    questionText: 'text-5xl',
  },
};

// The bottom of the curtain: nothing while there is nothing to report, a bar
// while the round's pictures come down, and a beat of "ready" when they have.
//
// The idle state used to be three bouncing dots on an infinite loop. The UX
// review (3 Nov 2026) asked for no looping or pulsing dots anywhere: the
// heading already says what is happening, and the bar below covers the one
// wait long enough to need a sign of life.
//
// Prod 4458 (19 Sep, slow Wi-Fi): a wine reveal showed nothing and the option
// icons half-loaded, because the curtain lifted on a timer while the pictures
// were still coming. Loading behind the curtain is half the fix; the other
// half is letting the room see it happening, so a slow connection reads as
// slow rather than broken — and so a fast one reads as fast rather than as a
// flicker. The three states cross-fade into each other for the same reason:
// swapping them instantly is what made the fast case look like a fault.
function LoadingState({ progress }) {
  const { phase, fraction } = useLoadingPhase(progress);
  const reduceMotion = prefersReducedMotion();
  // The states replace one another, so the outgoing one leaves before the next
  // arrives; a cross-fade in place would show two captions at once.
  const fade = reduceMotion
    ? { duration: 0 }
    : { duration: DUR.med, ease: 'easeOut' };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {phase === 'idle' ? null : (
        <Motion.div
          key="progress"
          className="mt-6 w-full max-w-xs"
          data-testid="curtain-progress"
          data-phase={phase}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fade}
        >
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/20">
            <Motion.div
              className="h-full rounded-full bg-white/80"
              data-testid="curtain-progress-fill"
              style={{ width: `${Math.round(fraction * 100)}%` }}
              initial={false}
              // The fill only ever moves forward (see useLoadingPhase) and it
              // always reaches the end before the block fades: on a fast
              // connection it would otherwise jump from nothing to gone
              // without being seen to fill at all.
              animate={{ width: `${Math.round(fraction * 100)}%` }}
              transition={reduceMotion ? { duration: 0 } : { duration: DUR.med, ease: 'easeOut' }}
            />
          </div>
          <div className="relative mt-2 h-4">
            <AnimatePresence mode="wait" initial={false}>
              <Motion.p
                key={phase}
                className="absolute inset-x-0 text-center text-xs text-white/70"
                // Fade only. The 3px slide this used to carry is movement
                // between two states that are already distinguishable by their
                // words, and on a phone with Reduce Motion it was stripped
                // anyway — so half the room saw a different transition from
                // the other half (Mel: "should be fade in and fade out, not
                // growing animations").
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={fade}
              >
                {phase === 'done' ? 'Ready' : "Loading this round's pictures"}
              </Motion.p>
            </AnimatePresence>
          </div>
        </Motion.div>
      )}
    </AnimatePresence>
  );
}

// While contextual copy (question text / leader name) is still loading, the
// curtain holds and fades the copy in when it arrives. It used to hold with
// the bouncing dots, then with plain green (UX review, 3 Nov 2026). Plain green
// is a loader too: over the pass-through ability step, where the next question
// is not this step's, a player watched an empty green screen for seconds
// (staging replay, 4 Nov 2026). So the hold says what comes next, in the same
// place and type as the line that replaces it, and stays still.
function CurtainHold({ heading, s }) {
  return (
    <div className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`} data-testid="curtain-hold">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,255,255,0.06) 0%, transparent 70%)' }}
      />
      <div className={s.panel}>
        <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
          {heading}
        </h2>
      </div>
    </div>
  );
}

function FadeIn({ children, className, style }) {
  return (
    <Motion.div
      className={className}
      style={style}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: DUR.med, ease: 'easeInOut' }}
    >
      {children}
    </Motion.div>
  );
}

const LEADER_LINES = [
  (name) => ({ heading: <>Keep it up,<br />{name}!</>, sub: "You're in the lead — don't let them catch you" }),
  (name) => ({ heading: <>{name}, stay<br />on top!</>, sub: 'Keep the momentum going' }),
  (name) => ({ heading: <>Hold your<br />ground, {name}!</>, sub: "They're coming for your crown" }),
];

const CHASER_LINES = [
  (name) => ({ heading: <>Don't let {name}<br />get away!</>, sub: 'Time to close the gap' }),
  (name) => ({ heading: <>Can you catch<br />{name}?</>, sub: "The leaderboard is about to shake up" }),
  (name) => ({ heading: <>{name} is pulling<br />ahead!</>, sub: "Now's your chance to strike back" }),
];

function pickLine(lines, name) {
  const idx = Math.floor(Math.random() * lines.length);
  return lines[idx](name);
}

// Tie / no-leader variants (QA 2026-07-14): an all-zero or shared top score
// must never crown an arbitrary player.
function scoreboardLineFor(leaderInfo) {
  if (leaderInfo.type === 'none') {
    return {
      heading: <>All square<br />so far!</>,
      sub: 'No one has broken away yet — this round could decide it',
    };
  }
  if (leaderInfo.type === 'tie') {
    if (leaderInfo.mine) {
      return {
        heading: <>You're tied<br />for the lead!</>,
        sub: 'One right answer breaks it',
      };
    }
    const names = leaderInfo.names || [];
    return {
      heading: names.length === 2
        ? <>{names[0]} and {names[1]}<br />are neck and neck!</>
        : <>It's a dead heat<br />at the top!</>,
      sub: 'Someone has to blink',
    };
  }
  return pickLine(leaderInfo.mine ? LEADER_LINES : CHASER_LINES, leaderInfo.name);
}

export default function TransitionCurtain({ step, questionText, wineNumber, leaderInfo, tvMode = false, progress, hydrating = false, rejoining = false, setupBy = null }) {
  const { gameMode, flowSteps, stepIndex } = useGameFlow();
  const config = FLOW_COPY[step?.type]?.transition ?? {};
  const s = tvMode ? SIZES.tv : SIZES.phone;

  let heading = config.heading;
  if (step?.type === 'wine-intro') {
    heading = wineNumber != null ? `Wine ${wineNumber} coming up` : 'Next wine coming up';
  }
  heading = heading ?? curtainFallbackHeading(step?.type);

  // A player (not the host) on one of the setup steps: their screen under the
  // curtain says "<host> is setting up the game…" on every one of them, so the
  // curtain says exactly that too, in its plainest form. Naming the step
  // ("Where the game shows") flipped the words back and forth between steps
  // that are, for a player, one screen (staging replay, 4 Nov 2026).
  // GameWrapper passes the host's name only then; the host's curtains and the
  // TV's never get it.
  const settingUp = !hydrating && Boolean(setupBy);
  if (settingUp) heading = settingUpLine(setupBy);

  // Until the server has said where the room is, `step` is a cache guess
  // (TAS-374) — so announcing its copy would tell the player they are about to
  // see something nobody has confirmed. This is also the whole wait now, not
  // just the tail of it: the separate "Getting back to the room" panel used to
  // run first, which is how one join ended up showing three screens in a row.
  if (hydrating) {
    heading = rejoining ? 'Getting back to the room' : 'Finding the room';
  }

  // The step's own contextual curtains (party question, rating, scoreboard)
  // only once the room's step is confirmed, and never over the setup line.
  // While hydrating, the party question curtain used to hold over a cache
  // guess with no words at all instead of saying "Finding the room".
  const contextual = !hydrating && !settingUp;
  const showQuestion = contextual && config.showQuestion && questionText;

  // Contextual question copy — { emoji, heading } derived from question text keywords.
  // Returns null when questionText hasn't loaded yet; the curtain then holds on "Next question".
  const questionCopy = useMemo(
    () => (step?.type === 'question' && gameMode === 'party' ? pickQuestionCurtainCopy(questionText) : null),
    [step?.type, gameMode, questionText]
  );

  // The scoreboard that goes dark gets its own curtain. The personalised line
  // below names whoever is leading, which is precisely the thing the next
  // screen is about to stop telling anyone — announcing the blackout over the
  // top of "you're still ahead, Sam" gives the standing away for free.
  const isBlackout = useMemo(
    () => isScoreboardBlackout(flowSteps, stepIndex),
    [flowSteps, stepIndex]
  );

  // Is this the very first scoreboard in the flow? If so use neutral copy.
  const isFirstScoreboard = useMemo(() => {
    if (step?.type !== 'scoreboard' || gameMode !== 'party' || !flowSteps) return false;
    return !flowSteps.slice(0, stepIndex).some(s2 => s2.type === 'scoreboard');
  }, [step?.type, gameMode, flowSteps, stepIndex]);

  // Personalised scoreboard copy — computed early so the hook always runs
  const scoreboardCopy = useMemo(() => {
    if (step?.type !== 'scoreboard' || gameMode !== 'party' || !leaderInfo) return null;
    return scoreboardLineFor(leaderInfo);
  }, [step?.type, gameMode, leaderInfo]);

  // Party question: contextual heading based on what the question is about.
  // Until the question text is in, the hold says "Next question".
  if (contextual && step?.type === 'question' && gameMode === 'party') {
    if (!questionCopy) return <CurtainHold heading={curtainFallbackHeading('question')} s={s} />;
    return (
      <FadeIn className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,255,255,0.06) 0%, transparent 70%)' }}
        />
        <div className={s.panel}>
          <div className={`${s.iconWrap} flex justify-center select-none`}><LessonActionIcon action={questionAction(questionText)} className={s.questionIcon} /></div>
          <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
            {questionCopy.heading}
          </h2>
          <p className={`font-display ${s.sub} italic text-white/70`}>
            Lock in your answers
          </p>
        </div>
      </FadeIn>
    );
  }

  // Wine review / rating screen transition
  if (contextual && step?.type === 'wine-review') {
    return (
      <div className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,255,255,0.06) 0%, transparent 70%)' }}
        />
        <div className={s.panel}>
          <div className={`${s.iconWrap} select-none flex justify-center`}>
            <img src={ratingImg} alt="Rate this wine" className={s.icon} />
          </div>
          {/* The control is a 0–5 slider, not thumbs (UX review, 3 Nov 2026). */}
          <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
            Time to rate<br />this wine
          </h2>
          <p className={`font-display ${s.sub} italic text-white/70`}>
            How was it? Slide to rate.
          </p>
        </div>
      </div>
    );
  }

  // The scoreboard is about to go dark — same beat in every mode.
  if (contextual && step?.type === 'scoreboard' && isBlackout) {
    return (
      <div className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(255,255,255,0.05) 0%, transparent 70%)' }}
        />
        <div className={s.panel}>
          <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
            One last thing<br />about the scoreboard
          </h2>
          <p className={`font-display ${s.sub} italic text-white/70`}>
            It's about to get interesting
          </p>
        </div>
      </div>
    );
  }

  // Party scoreboard curtain
  if (contextual && step?.type === 'scoreboard' && gameMode === 'party') {
    // First scoreboard — no one knows who's leading yet, show neutral copy immediately
    if (isFirstScoreboard) {
      return (
        <div className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`}>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
          />
          <div className={s.panel}>
            <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
              Let's see how<br />things shake out
            </h2>
            <p className={`font-display ${s.sub} italic text-white/70`}>
              First look at the standings
            </p>
          </div>
        </div>
      );
    }

    // Subsequent scoreboards — personalised — hold until leader info is ready
    if (!scoreboardCopy) return <CurtainHold heading={curtainFallbackHeading('scoreboard')} s={s} />;
    return (
      <FadeIn className={`fixed inset-0 flex flex-col items-center justify-center ${s.pad}`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
        />
        <div className={s.panel}>
          <div className={`${s.iconWrap} select-none flex justify-center`}>
            {/* Fallback reserves the icon's box so the heading does not shift
                when the chunk lands — sized from the same token as the icon,
                or the TV would reserve 64px and then land a 176px icon. */}
            <Suspense fallback={<span className={`block ${s.leaderIcon}`} aria-hidden="true" />}>
              <FirstIcon className={s.leaderIcon} alt="Leader" />
            </Suspense>
          </div>
          <h2 className={`font-display ${s.heading} font-bold text-white leading-tight`}>
            {scoreboardCopy.heading}
          </h2>
          <p className={`font-display ${s.sub} italic text-white/70`}>
            {scoreboardCopy.sub}
          </p>
        </div>
      </FadeIn>
    );
  }

  return (
    <div className={`fixed inset-0 flex flex-col items-center justify-center ${s.defaultPad}`}>
      {/* The vignette the party curtains already wear. The default curtain is
          the one that hands over to Game's "All players have submitted" screen
          and RevealAnswers' overlay, so it carries the same surface. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,255,255,0.06) 0%, transparent 70%)' }}
      />
      <div className={`relative text-center ${s.column}`}>
        {!hydrating && showQuestion && (
          <div className={`${s.iconWrap} flex justify-center`}>
            <LessonActionIcon action={questionAction(questionText)} className={s.questionIcon} />
          </div>
        )}
        {/* Heading */}
        <h1 className={`font-display ${s.defaultHeading} text-balance text-white`}>
          {heading}
        </h1>

        {/* Question card */}
        {showQuestion && (
          <div className={`${s.cardBorder} border-white/20 ${s.questionCard} bg-white/[0.07] backdrop-blur-sm`}>
            <p className={`${s.questionText} text-balance text-white/90 font-medium leading-snug`}>
              {questionText}
            </p>
          </div>
        )}
      </div>

      {/* What the wait is for.
          Prod 4458 (19 Sep, slow Wi-Fi): a wine reveal showed nothing and the
          option icons half-loaded, because the curtain lifted on a timer while
          the pictures were still coming. Loading behind the curtain is half the
          fix; the other half is letting the room see it happening, so a slow
          connection reads as slow rather than broken. */}
      <LoadingState progress={progress} />
    </div>
  );
}
